/**
 * Timeline tổng hợp 2 nguồn cho phiên Virtual Courtroom:
 *  - Hearing minutes entries: do Thư ký ghi tay (event_type chuẩn của Moot Court)
 *  - Macro events: hệ thống tự ghi (courtroom.start, evidence_shared, verdict_submitted,
 *    timer_paused, participant_kicked/muted, surprise_event...)
 *
 * UI: Accordion thu gọn trong panel meet. Mở ra hiển thị sự kiện chronological (cũ → mới),
 * filter theo nguồn (manual/auto/cả hai), với icon + thời gian + actor.
 *
 * Thư ký có thể "Mở biên bản" (nếu chưa có) + "Thêm sự kiện" + "Đóng băng (finalize)".
 */
import { useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ExpandMore,
  Gavel,
  History,
  Mic,
  PlayArrow,
  Stop,
  Description,
  PersonRemove,
  MicOff,
  Bolt,
  Add,
  Lock,
} from '@mui/icons-material'

import {
  useGetMinuteForCourtroomQuery,
  useOpenMinuteMutation,
  useAddMinuteEntryMutation,
  useFinalizeMinuteMutation,
  type EventType,
  type HearingMinuteEntry,
} from '../store/api/hearingMinutesApi'
import {
  useGetCourtroomMacroEventsQuery,
  type CourtroomMacroEvent,
} from '../store/api/courtroomsApi'

const EVENT_LABELS: Record<EventType, string> = {
  session_open: 'Khai mạc phiên',
  team_intro: 'Giới thiệu đội',
  evidence_disclosed: 'Công bố chứng cứ',
  argument: 'Tranh luận',
  judge_question: 'Câu hỏi HĐXX',
  team_response: 'Đội phản hồi',
  objection: 'Phản đối',
  recess: 'Nghỉ giải lao',
  closing_statement: 'Phát biểu tổng kết',
  verdict_delivered: 'Tuyên án',
  session_close: 'Bế mạc phiên',
  other: 'Khác',
}

const ICON_BY_MACRO: Record<string, React.ReactNode> = {
  'courtroom.start': <PlayArrow fontSize="small" color="success" />,
  'courtroom.completed': <Stop fontSize="small" color="error" />,
  'courtroom.evidence_shared': <Description fontSize="small" color="info" />,
  'courtroom.stop_sharing': <Description fontSize="small" />,
  'courtroom.verdict_submitted': <Gavel fontSize="small" color="warning" />,
  'courtroom.verdict_published': <Gavel fontSize="small" color="success" />,
  'courtroom.participant_kicked': <PersonRemove fontSize="small" color="error" />,
  'courtroom.participant_muted': <MicOff fontSize="small" color="warning" />,
  'courtroom.surprise_event': <Bolt fontSize="small" color="warning" />,
  'courtroom.recording_start': <Mic fontSize="small" color="error" />,
  'courtroom.recording_stop': <Mic fontSize="small" />,
}

type Source = 'manual' | 'auto'
type TimelineItem = {
  ts: string
  source: Source
  icon: React.ReactNode
  title: string
  content?: string
  actor?: string | null
  raw: HearingMinuteEntry | CourtroomMacroEvent
  // Cho việc xoá entry manual
  entryIndex?: number
}

export interface CourtroomTimelinePanelProps {
  courtroomId: string
  caseId: string
  roundId: string
  canModerate: boolean // secretary/admin
  defaultExpanded?: boolean
}

export function CourtroomTimelinePanel({
  courtroomId,
  caseId,
  roundId,
  canModerate,
  defaultExpanded = false,
}: CourtroomTimelinePanelProps) {
  const {
    data: minute,
    isLoading: loadingMinute,
    refetch: refetchMinute,
  } = useGetMinuteForCourtroomQuery(courtroomId, {
    pollingInterval: 30_000, // Refresh nhẹ mỗi 30s khi panel mở
  })
  const { data: macroEvents = [], isLoading: loadingMacro } = useGetCourtroomMacroEventsQuery(
    { courtroomId, limit: 500 },
    { pollingInterval: 30_000 },
  )

  const [openMinute, { isLoading: opening }] = useOpenMinuteMutation()
  const [addEntry, { isLoading: addingEntry }] = useAddMinuteEntryMutation()
  const [finalizeMinute, { isLoading: finalizing }] = useFinalizeMinuteMutation()

  const [filter, setFilter] = useState<'all' | Source>('all')
  const [addDialog, setAddDialog] = useState(false)
  const [eventType, setEventType] = useState<EventType>('argument')
  const [content, setContent] = useState('')
  const [actorLabel, setActorLabel] = useState('')
  const [finalizeDialog, setFinalizeDialog] = useState(false)
  const [summary, setSummary] = useState('')

  const items = useMemo<TimelineItem[]>(() => {
    const out: TimelineItem[] = []
    if (minute?.entries) {
      minute.entries.forEach((e, idx) => {
        out.push({
          ts: e.timestamp,
          source: 'manual',
          icon: <History fontSize="small" color="primary" />,
          title: EVENT_LABELS[e.event_type] || e.event_type,
          content: e.content,
          actor: e.actor_label || e.actor || null,
          raw: e,
          entryIndex: idx,
        })
      })
    }
    macroEvents.forEach((m) => {
      out.push({
        ts: m.ts || new Date().toISOString(),
        source: 'auto',
        icon: ICON_BY_MACRO[m.event_type] || <History fontSize="small" />,
        title: m.event_type.replace(/^courtroom\./, ''),
        content: m.payload && Object.keys(m.payload).length > 0
          ? JSON.stringify(m.payload).slice(0, 200)
          : undefined,
        actor: m.actor_role
          ? `${m.actor_role}${m.actor_id ? ' (' + m.actor_id.slice(0, 6) + ')' : ''}`
          : (m.actor_id || null),
        raw: m,
      })
    })
    // Filter
    const filtered =
      filter === 'all' ? out : out.filter((x) => x.source === filter)
    // Sort cũ → mới
    filtered.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0))
    return filtered
  }, [minute, macroEvents, filter])

  const handleOpen = async () => {
    try {
      await openMinute({
        courtroom_id: courtroomId,
        case_id: caseId,
        round_id: roundId,
      }).unwrap()
    } catch (e: any) {
      alert(`Mở biên bản thất bại: ${e?.data?.detail || e?.message}`)
    }
  }

  const handleAdd = async () => {
    if (!minute?.id) return
    if (!content.trim()) return
    try {
      await addEntry({
        minute_id: minute.id,
        courtroom_id: courtroomId,
        entry: {
          event_type: eventType,
          content: content.trim(),
          actor_label: actorLabel.trim() || undefined,
        },
      }).unwrap()
      setAddDialog(false)
      setContent('')
      setActorLabel('')
    } catch (e: any) {
      alert(`Thêm entry thất bại: ${e?.data?.detail || e?.message}`)
    }
  }

  const handleFinalize = async () => {
    if (!minute?.id) return
    try {
      await finalizeMinute({
        minute_id: minute.id,
        courtroom_id: courtroomId,
        summary: summary.trim() || undefined,
      }).unwrap()
      setFinalizeDialog(false)
      setSummary('')
      refetchMinute()
    } catch (e: any) {
      alert(`Đóng băng biên bản thất bại: ${e?.data?.detail || e?.message}`)
    }
  }

  const isFinalized = minute?.status === 'finalized'
  const loading = loadingMinute || loadingMacro

  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mt: 1, borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <History fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            Biên bản & Timeline phiên
          </Typography>
          <Chip
            size="small"
            label={`${items.length} sự kiện`}
            variant="outlined"
            sx={{ ml: 1 }}
          />
          {isFinalized && (
            <Chip size="small" label="Đã đóng băng" color="success" icon={<Lock fontSize="small" />} />
          )}
          {!minute && !loading && (
            <Chip size="small" label="Chưa mở biên bản" color="warning" variant="outlined" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Nguồn</InputLabel>
            <Select label="Nguồn" value={filter} onChange={(e) => setFilter(e.target.value as any)}>
              <MenuItem value="all">Tất cả</MenuItem>
              <MenuItem value="manual">Thư ký ghi</MenuItem>
              <MenuItem value="auto">Hệ thống tự</MenuItem>
            </Select>
          </FormControl>
          {canModerate && !minute && (
            <Button
              size="small"
              variant="outlined"
              startIcon={opening ? <CircularProgress size={14} /> : <PlayArrow />}
              onClick={handleOpen}
              disabled={opening}
            >
              Mở biên bản
            </Button>
          )}
          {canModerate && minute && !isFinalized && (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddDialog(true)}
                disabled={addingEntry}
              >
                Thêm sự kiện
              </Button>
              <Button
                size="small"
                color="warning"
                variant="outlined"
                startIcon={<Lock />}
                onClick={() => setFinalizeDialog(true)}
                disabled={finalizing}
              >
                Đóng băng
              </Button>
            </>
          )}
        </Stack>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            Chưa có sự kiện nào. {canModerate && !minute && 'Bấm "Mở biên bản" để bắt đầu.'}
          </Typography>
        )}

        <Stack spacing={1}>
          {items.map((item, idx) => (
            <Box
              key={`${item.source}-${idx}-${item.ts}`}
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
                p: 1,
                borderRadius: 1,
                bgcolor: item.source === 'manual' ? 'rgba(20,78,140,0.04)' : 'transparent',
              }}
            >
              <Box sx={{ pt: 0.5 }}>{item.icon}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.ts).toLocaleString('vi-VN')}
                  </Typography>
                  <Chip
                    size="small"
                    label={item.source === 'manual' ? 'Thư ký' : 'Hệ thống'}
                    color={item.source === 'manual' ? 'primary' : 'default'}
                    variant="outlined"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {item.title}
                  </Typography>
                  {item.actor && (
                    <Tooltip title={item.actor}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        bởi {item.actor}
                      </Typography>
                    </Tooltip>
                  )}
                </Stack>
                {item.content && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {item.content}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>

      {/* Dialog: Thêm entry */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Thêm sự kiện vào biên bản</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Loại sự kiện</InputLabel>
              <Select
                label="Loại sự kiện"
                value={eventType}
                onChange={(e) => setEventType(e.target.value as EventType)}
              >
                {Object.entries(EVENT_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              minRows={3}
              required
              label="Nội dung"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <TextField
              fullWidth
              label="Người thực hiện (tuỳ chọn)"
              placeholder="VD: Nguyên đơn / GK Chủ tọa / Đội Bảo vệ ..."
              value={actorLabel}
              onChange={(e) => setActorLabel(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>Hủy</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!content.trim() || addingEntry}
          >
            {addingEntry ? <CircularProgress size={16} /> : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Finalize */}
      <Dialog open={finalizeDialog} onClose={() => setFinalizeDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Đóng băng biên bản</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Sau khi đóng băng, không thể thêm/xoá entry. Hành động không thể hoàn tác.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Tóm tắt (tuỳ chọn)"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizeDialog(false)}>Hủy</Button>
          <Button color="warning" variant="contained" onClick={handleFinalize} disabled={finalizing}>
            {finalizing ? <CircularProgress size={16} /> : 'Xác nhận đóng băng'}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  )
}
